import * as React from 'react';
import * as Yup from 'yup';
import { styled } from '@mui/material/styles';
import { Backdrop, Grow, Popper } from '@mui/material';
import { Paper, Stack } from '@mui/material';
import { useFormik } from 'formik';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { useGetCubeDataDetailOptions } from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import { getListBoxPropsAutoScrolItem } from 'utils/Object';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ButtonLoading from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

// ─── Styled Popper with arrow ────────────────────────────────────────────────

const StyledPopper = styled(Popper)(() => ({
  zIndex: 1000,
  '&[data-popper-placement*="bottom"] .popper-arrow': {
    top: 0,
    marginTop: '-0.71em',
    '&::before': {
      transformOrigin: '0 100%',
    },
  },
  '&[data-popper-placement*="top"] .popper-arrow': {
    bottom: 0,
    marginBottom: '-0.71em',
    '&::before': {
      transformOrigin: '100% 0',
    },
  },
  '&[data-popper-placement*="right"] .popper-arrow': {
    left: 0,
    marginLeft: '-0.71em',
    height: '1em',
    width: '0.71em',
    '&::before': {
      transformOrigin: '100% 100%',
    },
  },
  '&[data-popper-placement*="left"] .popper-arrow': {
    right: 0,
    marginRight: '-0.71em',
    height: '1em',
    width: '0.71em',
    '&::before': {
      transformOrigin: '0 0',
    },
  },
}));

const PopperArrow = styled('span')(() => ({
  overflow: 'hidden',
  position: 'absolute',
  width: '1em',
  height: '0.71em',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: '100%',
    height: '100%',
    backgroundColor: '#fff',
    transform: 'rotate(45deg)',
    boxShadow: '0px 0px 4px rgba(0,0,0,0.15)',
  },
}));

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormCubeDrillThruParameter {
  member: AutoCompleteItem | null;
  timePeriod: AutoCompleteItem | null;
  eventSubject: AutoCompleteItem | null;
}

// ─── Validation ───────────────────────────────────────────────────────────────

const tableAnalyticParameterValidatonSchema = Yup.object().shape({
  member: Yup.object().required('Required'),
  timePeriod: Yup.object().required('Required'),
  eventSubject: Yup.object().required('Required'),
});

const formikInitialValues: FormCubeDrillThruParameter = {
  member: null,
  timePeriod: null,
  eventSubject: null,
};

interface ButtonCubeDrillThruProps {
  onDisplayData?: (data: FormCubeDrillThruParameter) => void;
  disabled?: boolean | undefined;
  cubeId?: string;
  initialValue?: {
    member: AutoCompleteItem | null;
    timePeriod: AutoCompleteItem | null;
  };
}

// ─── Autocomplete styles (matching sibling components) ────────────────────────

const autocompleteStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function ButtonCubeDrillThru(props: ButtonCubeDrillThruProps) {
  const { onDisplayData, disabled, cubeId, initialValue } = props;

  const { data: cubeDetailDataOptions } = useGetCubeDataDetailOptions(cubeId);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [arrowRef, setArrowRef] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const formik = useFormik<FormCubeDrillThruParameter>({
    initialValues: formikInitialValues,
    validationSchema: tableAnalyticParameterValidatonSchema,
    onSubmit: (values, formikHelper) => {
      handlePopoverClose();
      onDisplayData && onDisplayData(values);
      formikHelper.resetForm();
    },
  });

  React.useEffect(() => {
    if (initialValue && open) {
      formik.setFieldValue('member', initialValue.member);
      formik.setFieldValue('timePeriod', initialValue.timePeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, open]);

  return (
    <div>
      <Backdrop open={open} sx={{ zIndex: 999 }} />

      <Tooltip title='Data Details'>
        <span data-testid='button-export-label'>
          <IconButton data-testid='button-export' onClick={handlePopoverOpen} disabled={disabled}>
            <TableChartOutlinedIcon sx={{ color: disabled ? '#ccc' : '#42BB93' }} />
          </IconButton>
        </span>
      </Tooltip>

      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement='left-start'
        disablePortal={false}
        transition
        modifiers={[
          { name: 'flip', options: { altBoundary: true, rootBoundary: 'document', padding: 8 } },
          { name: 'preventOverflow', options: { altAxis: true, tether: true, padding: 8 } },
          { name: 'arrow', options: { element: arrowRef } },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={250}>
            <div>
              <PopperArrow className='popper-arrow' ref={setArrowRef} />

              <Paper
                elevation={8}
                sx={{
                  width: 380,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                }}
              >
                {/* Header */}
                <Stack sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                  <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                    <Stack>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#3B4797', lineHeight: 1.3 }}>
                        Data Details
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#98A2AE', mt: 0.25 }}>
                        Select parameters to view detailed data
                      </Typography>
                    </Stack>
                    <IconButton size='small' onClick={handlePopoverClose} sx={{ mt: -0.5 }}>
                      <HighlightOffRoundedIcon sx={{ color: '#98A2AE', fontSize: 20 }} />
                    </IconButton>
                  </Stack>
                </Stack>
                <Divider />

                {/* Body */}
                <Stack sx={{ px: 2, py: 1.5 }}>
                  {/* Member */}
                  <Stack mb={1.5}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 0.25 }}>Member</Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#98A2AE', mb: 0.75 }}>
                      Select the member to inspect
                    </Typography>
                    <Autocomplete
                      size='small'
                      options={cubeDetailDataOptions?.data?.memberOptions || []}
                      value={formik.values.member}
                      sx={autocompleteStyles}
                      onChange={(_event, newValue) => {
                        formik.setFieldValue('member', newValue);
                      }}
                      ListboxProps={{
                        ...getListBoxPropsAutoScrolItem(
                          cubeDetailDataOptions?.data?.memberOptions || [],
                          formik.values.member
                        ),
                      }}
                      clearIcon={null}
                      renderInput={(params) => <TextField {...params} placeholder='Select Member...' />}
                    />
                  </Stack>

                  {/* Week */}
                  <Stack mb={1.5}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 0.25 }}>Week</Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#98A2AE', mb: 0.75 }}>
                      Choose the time period
                    </Typography>
                    <Autocomplete
                      size='small'
                      options={cubeDetailDataOptions?.data?.timePeriodOptions || []}
                      value={formik.values.timePeriod}
                      defaultValue={initialValue?.timePeriod}
                      sx={autocompleteStyles}
                      onChange={(_event, newValue) => {
                        formik.setFieldValue('timePeriod', newValue);
                      }}
                      ListboxProps={{
                        ...getListBoxPropsAutoScrolItem(
                          cubeDetailDataOptions?.data?.timePeriodOptions || [],
                          formik.values.timePeriod
                        ),
                      }}
                      clearIcon={null}
                      renderInput={(params) => <TextField {...params} placeholder='Select Week...' />}
                    />
                  </Stack>

                  {/* Data */}
                  <Stack mb={2}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 0.25 }}>Data</Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#98A2AE', mb: 0.75 }}>
                      Pick the data attribute to display
                    </Typography>
                    <Autocomplete
                      size='small'
                      options={cubeDetailDataOptions?.data?.attributeOptions || []}
                      value={formik.values.eventSubject}
                      sx={autocompleteStyles}
                      onChange={(_event, newValue) => {
                        formik.setFieldValue('eventSubject', newValue);
                      }}
                      clearIcon={null}
                      renderInput={(params) => <TextField {...params} placeholder='Select Data...' />}
                    />
                  </Stack>

                  {/* Footer actions */}
                  <Stack direction='row' spacing={1.5} mt={3} justifyContent='flex-end'>
                    <Button size='small' onClick={handlePopoverClose} variant='main-table-panel-border'>
                      Cancel
                    </Button>
                    <ButtonLoading
                      size='small'
                      variant='main-table-panel'
                      onClick={() => formik.handleSubmit()}
                      disabled={!tableAnalyticParameterValidatonSchema.isValidSync(formik.values) || disabled}
                    >
                      Display
                    </ButtonLoading>
                  </Stack>
                </Stack>
              </Paper>
            </div>
          </Grow>
        )}
      </StyledPopper>
    </div>
  );
}
